import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Download, Smartphone, Zap, Trophy, Gem, Share, Plus } from 'lucide-react'
import { trackPWAInstallClick, trackPWAInstallDismissed, trackPWAPromptShown } from '../services/trackingService'
import { LOGO_SRC } from '../config/brand'

const Install = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const deferredPromptRef = useRef(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalling, setIsInstalling] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)

  useEffect(() => {
    // Проверка — уже запущено как PWA (standalone)
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true
    setIsStandalone(standalone)

    if (standalone) {
      navigate('/', { replace: true })
      return
    }

    // Проверка iOS (нет beforeinstallprompt)
    const ua = window.navigator.userAgent
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream
    setIsIOS(ios)

    // Проверяем глобально перехваченный промпт (из index.html)
    if (window.__deferredPrompt) {
      deferredPromptRef.current = window.__deferredPrompt
      setIsInstallable(true)
      trackPWAPromptShown()
    }

    // Ловим beforeinstallprompt, если ещё не перехвачен глобально
    let promptTracked = Boolean(window.__deferredPrompt)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault()
      deferredPromptRef.current = e
      window.__deferredPrompt = e
      setIsInstallable(true)
      if (!promptTracked) {
        trackPWAPromptShown()
        promptTracked = true
      }
    }

    // Слушаем глобальное событие (если промпт перехвачен до монтирования компонента)
    const handleInstallAvailable = () => {
      if (window.__deferredPrompt) {
        deferredPromptRef.current = window.__deferredPrompt
        setIsInstallable(true)
        if (!promptTracked) {
          trackPWAPromptShown()
          promptTracked = true
        }
      }
    }

    // Ловим appinstalled
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setIsInstallable(false)
      deferredPromptRef.current = null
      setTimeout(() => {
        navigate('/welcome', { replace: true })
      }, 2000)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
    window.addEventListener('pwa-install-available', handleInstallAvailable)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
      window.removeEventListener('pwa-install-available', handleInstallAvailable)
    }
  }, [navigate])

  const handleInstallClick = async () => {
    const deferredPrompt = deferredPromptRef.current

    if (!deferredPrompt) {
      return
    }

    setIsInstalling(true)
    trackPWAInstallClick()

    try {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice

      if (outcome === 'accepted') {
        // trackPWAInstall вызовется из глобального обработчика appinstalled в App.jsx
      } else {
        trackPWAInstallDismissed()
      }

      deferredPromptRef.current = null
      setIsInstallable(false)
    } catch (error) {
      console.error('Ошибка установки PWA:', error)
    } finally {
      setIsInstalling(false)
    }
  }

  const features = [
    { icon: Zap, title: t('install.featureFreeCase'), text: '' },
    { icon: Trophy, title: t('install.featureFreeUpgrade'), text: '' },
    { icon: Gem, title: t('install.featureFastWithdraw'), text: '' },
  ]

  return (
    <div className="min-h-screen bg-dark-primary text-white overflow-hidden">
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-10 relative">
        {/* Фоновые акценты */}
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-accent-primary/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-56 -left-56 w-[640px] h-[640px] rounded-full bg-accent-secondary/5 blur-3xl pointer-events-none" />

        <div className="relative z-10 w-full max-w-md flex flex-col items-center text-center">
          {/* Логотип */}
          <div className="relative inline-block mb-6">
            <img
              src={LOGO_SRC}
              alt="SKINOZ"
              className="relative z-10 mx-auto w-full max-w-[220px]"
            />
            <div className="pointer-events-none absolute left-1/2 top-full h-8 w-3/4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-primary/25 blur-2xl" />
          </div>

          {/* Заголовок */}
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">
            {t('install.title')}
          </h1>
          {/* <p className="text-text-secondary text-base sm:text-lg mb-8">
            SKINOZ — открывай кейсы, улучшай скины и выигрывай CS2 предметы прямо со своего телефона
          </p> */}

          {/* Преимущества */}
          <div className="grid grid-cols-1 gap-3 w-full mb-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/[0.06]"
              >
                <div className="w-10 h-10 rounded-xl bg-accent-primary/10 flex items-center justify-center shrink-0">
                  <feature.icon size={20} className="text-accent-primary" />
                </div>
                <div className="text-left">
                  <div className="font-bold text-sm">{feature.title}</div>
                  <div className="text-text-secondary text-xs">{feature.text}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Кнопка установки */}
          {isInstalled ? (
            <div className="w-full px-5 py-4 rounded-2xl bg-accent-primary/15 border border-accent-primary/30 text-accent-primary font-bold">
              {t('install.installedRedirecting')}
            </div>
          ) : isStandalone ? (
            <div className="w-full px-5 py-4 rounded-2xl bg-white/[0.04] border border-white/[0.06] text-text-secondary">
              {t('install.alreadyInstalled')}
            </div>
          ) : isIOS ? (
            /* iOS — нет beforeinstallprompt, показываем инструкцию */
            <div className="w-full">
              <div className="px-5 py-4 rounded-2xl bg-white/[0.04] border border-white/[0.06] text-left">
                <div className="font-bold text-sm mb-2 text-center">
                  {t('install.iosTitle')}
                </div>
                <ol className="space-y-2 text-text-secondary text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-accent-primary font-bold">1.</span>
                    <span>{t('install.iosStep1')}</span>
                    <Share size={16} className="text-accent-primary mt-0.5" />
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-primary font-bold">2.</span>
                    <span>{t('install.iosStep2')}</span>
                    <Plus size={16} className="text-accent-primary mt-0.5" />
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-primary font-bold">3.</span>
                    <span>{t('install.iosStep3')}</span>
                  </li>
                </ol>
              </div>
              <button
                onClick={() => navigate('/welcome', { replace: true })}
                className="mt-4 w-full px-5 py-4 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.09] to-white/[0.04] font-bold text-sm transition-all duration-300 hover:-translate-y-0.5"
              >
                {t('install.continueWithoutInstall')}
              </button>
            </div>
          ) : isInstallable ? (
            <button
              onClick={handleInstallClick}
              disabled={isInstalling}
              className="group block w-full px-5 py-4 rounded-2xl border border-accent-primary/40 bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(0,255,127,0.25)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-center gap-3">
                <Download size={22} className="text-accent-primary" />
                <div className="text-lg font-bold tracking-tight">
                  {isInstalling ? t('install.installing') : t('install.installButton')}
                </div>
              </div>
            </button>
          ) : (
            /* Браузер не поддерживает установку или промпт ещё не сработал */
            <div className="w-full">
              <div className="px-5 py-4 rounded-2xl bg-white/[0.04] border border-white/[0.06] text-text-secondary text-sm mb-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Smartphone size={18} className="text-accent-primary" />
                  <span className="font-bold text-white">{t('install.bookmarkTitle')}</span>
                </div>
                {t('install.bookmarkText')}
              </div>
              <button
                onClick={() => navigate('/welcome', { replace: true })}
                className="w-full px-5 py-4 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.09] to-white/[0.04] font-bold text-sm transition-all duration-300 hover:-translate-y-0.5"
              >
                {t('common.continue')}
              </button>
            </div>
          )}

          {/* Ссылка на вход */}
          {!isInstalled && !isStandalone && (
            <button
              onClick={() => navigate('/welcome', { replace: true })}
              className="mt-4 text-text-secondary text-sm hover:text-accent-primary transition-colors"
            >
              {t('install.continueOnSite')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Install
